import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, useWindowDimensions } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { SceneRendererProps, TabBar, TabDescriptor, TabView } from 'react-native-tab-view';

import { useFocusEffect } from '@react-navigation/native';

import { LeagueConfigsFragment } from '@/api/leagues/query.generated';
import { Loading } from '@/components/Loading';
import { TopTabBarLabel } from '@/components/TopTabBar';
import { Box } from '@/components/lib/components/Box';
import { ds } from '@/components/lib/design.system';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useLobbySectionSelection } from '@/feature/lobby/hooks/use-lobby-section-selection';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { common } from '@/styles/styles';
import { League } from '@/types/api.generated';
import { MAX_WEB_WIDTH, isWeb } from '@/utils/constants-platform-specific';

import { PicksSearchResults } from '../components/PicksSearchResults';
import { useAvailableLeagues } from '../hooks/use-available-leagues';
import { SearchScreenShell, useSearchScreen } from './SearchScreenShell';

type Route = {
    key: League;
    title: string;
    label: string;
};

type NavigationState = {
    index: number;
    routes: Route[];
};

export const PicksSearchScreen = () => {
    useFocusEffect(
        useCallback(() => {
            BetrAnalytics.trackEvent(AnalyticsEvent.SEARCH_OPEN);
        }, [])
    );

    return (
        <SearchScreenShell>
            <LeagueSearchTabs />
        </SearchScreenShell>
    );
};

const LeagueSearchTabs = () => {
    const { debouncedQuery: query, collapseSearchBar } = useSearchScreen();
    const leagues = useAvailableLeagues();
    const selectedTile = useLobbySectionSelection(state => state.selectedTile);

    const [index, setIndex] = useState(() => {
        const selectedLeagueIndex = leagues.findIndex(league => league.league === selectedTile);
        if (selectedLeagueIndex !== -1) {
            return selectedLeagueIndex;
        }
        return 0;
    });

    const routes: Route[] = useMemo(() => {
        return leagues.map(league => ({
            key: league.league,
            title: league.label,
            label: league.label,
        }));
    }, [leagues]);

    const { width } = useWindowDimensions();

    useEffect(() => {
        if (leagues?.[index]) {
            BetrAnalytics.trackEvent(AnalyticsEvent.SEARCH_LEAGUE_SELECTED, { league: leagues[index].league });
        }
    }, [index, leagues]);

    const scenes = useCallback(
        ({ route }: { route: Route }) => renderScene(route, query, collapseSearchBar),
        [query, collapseSearchBar]
    );

    const commonOptions = useMemo(
        () => ({
            label: renderTabBarLabel,
        }),
        []
    );

    return (
        <Box style={common.flex}>
            {leagues.length === 0 ? (
                <MaxWidthWrapper flex={1} justifyContent="center" alignItems={'center'}>
                    <Loading />
                </MaxWidthWrapper>
            ) : (
                <TabView
                    navigationState={{ index, routes }}
                    renderScene={scenes}
                    lazy={true}
                    renderLazyPlaceholder={() => (
                        <Box flex={1} justifyContent="center" alignItems="center">
                            <Loading />
                        </Box>
                    )}
                    onIndexChange={setIndex}
                    renderTabBar={props => {
                        if (isWeb) {
                            return (
                                <MaxWidthWrapper>
                                    <ScrollView horizontal>{renderTabBar(props, width, leagues)}</ScrollView>
                                </MaxWidthWrapper>
                            );
                        }
                        return renderTabBar(props, width, leagues);
                    }}
                    initialLayout={{ width }}
                    keyboardDismissMode="none"
                    commonOptions={commonOptions}
                />
            )}
        </Box>
    );
};

const renderScene = (route: Route, query: string, onScrollBeginDrag: () => void) => {
    return (
        <MaxWidthWrapper flex={1}>
            <PicksSearchResults query={query} league={route.key} onScrollBeginDrag={onScrollBeginDrag} />
        </MaxWidthWrapper>
    );
};

const renderTabBar = (
    props: SceneRendererProps & {
        navigationState: NavigationState;
    },
    screenWidth: number,
    leagues: LeagueConfigsFragment[]
) => {
    const maxScreenWidth = screenWidth > MAX_WEB_WIDTH ? MAX_WEB_WIDTH : screenWidth;
    return (
        <TabBar
            {...props}
            scrollEnabled
            style={[styles.tabBarStyle]}
            indicatorStyle={styles.indicator}
            tabStyle={[styles.tabStyle, { minWidth: maxScreenWidth / leagues.length }]}
        />
    );
};

const renderTabBarLabel: TabDescriptor<Route>['label'] = ({ route, focused }) => {
    return <TopTabBarLabel focused={focused}>{route.label}</TopTabBarLabel>;
};

const styles = StyleSheet.create({
    indicator: {
        height: 4,
        backgroundColor: ds.colorValue.background.neutral.inverse,
        marginBottom: -4,
    },
    tabBarStyle: {
        backgroundColor: ds.colorValue.background.neutral.base,
        borderBottomWidth: 4,
        borderBottomColor: ds.colorValue.border.strong,
    },
    tabStyle: {
        flexGrow: 1,
        width: 'auto',
        paddingHorizontal: 16,
        height: 48,
    },
});
